<template>
  <ResponsiveLayout :progress="progress">
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__prescription_question__title') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ $t('eu__prescription_question__description') }}
        </p>
      </div>
      <fieldset>
        <div
          :class="[
            'quiz-radio',
            { 'quiz-radio--checked': hasPrescription === true },
          ]"
          @click="selectPrescription(true)"
        >
          <label class="quiz-radio__label kds-typography-title-medium">
            {{ $t('eu__prescription_question__has_prescription__title') }}
          </label>
          <div class="quiz-radio__layout">
            <CustomRadio
              :checked="hasPrescription === true"
              data-qa="prescription-question-has-prescription"
              value="yes"
            />
          </div>
        </div>
        <div
          :class="[
            'quiz-radio',
            { 'quiz-radio--checked': hasPrescription === false },
          ]"
          @click="selectPrescription(false)"
        >
          <label class="quiz-radio__label kds-typography-title-medium">
            {{ $t('eu__prescription_question__no_prescription__title') }}
          </label>
          <div class="quiz-radio__layout">
            <CustomRadio
              :checked="hasPrescription === false"
              data-qa="prescription-question-no-prescription"
              value="no"
            />
          </div>
        </div>
      </fieldset>

      <template v-if="hasPrescription === true">
        <div class="kds-content-block">
          <p class="kds-typography-title-xlarge">
            {{ $t('eu__prescription_question__code_question__title') }}
          </p>
          <BaseBottomSheet
            :title="$t('eu__code_question__help_title')"
            screen-name="prescription_question_code_help_bottom_sheet"
          >
            <template #trigger="{ open }">
              <BaseLink
                :text="$t('eu__code_question__help_trigger')"
                @click="open"
              >
                <template #icon>
                  <InfoIcon />
                </template>
              </BaseLink>
            </template>
            <div v-html="$t('eu__code_question__help_content')" />
          </BaseBottomSheet>
        </div>
        <fieldset>
          <div
            :class="['quiz-radio', { 'quiz-radio--checked': hasCode === true }]"
            @click="selectCode(true)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t('eu__prescription_question__has_code__title') }}
            </label>
            <div class="quiz-radio__layout">
              <CustomRadio
                :checked="hasCode === true"
                data-qa="prescription-question-has-code"
                value="yes"
              />
            </div>
          </div>
          <div
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': hasCode === false },
            ]"
            @click="selectCode(false)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t('eu__prescription_question__no_code__title') }}
            </label>
            <div class="quiz-radio__layout">
              <CustomRadio
                :checked="hasCode === false"
                data-qa="prescription-question-no-code"
                value="no"
              />
            </div>
          </div>
        </fieldset>
      </template>
    </div>

    <template #actions>
      <BaseButton
        :text="$t('generic_action_continue')"
        :disabled="!canContinue"
        data-qa="submit"
        @click="handleContinue"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useEuStore } from '@eu/app/stores/useEuStore.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseBottomSheet from '@shared/components/BaseBottomSheet.vue'
import BaseLink from '@shared/components/BaseLink.vue'
import CustomRadio from '@shared/components/CustomRadio.vue'
import InfoIcon from '@shared/assets/icon/InfoIcon.vue'
import Tracker from '@shared/Tracker.js'
import * as apiRequest from '@shared/api-endpoints/index.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
const { progress } = useOnboardingProgress()
const { navigateNext } = useNavigation(ROUTE.PRESCRIPTION_QUESTION)
const euStore = useEuStore()

const hasPrescription = ref(
  euStore.prescriptionQuestionAnswers.hasPrescription ?? false,
)
const hasCode = ref(euStore.prescriptionQuestionAnswers.hasCode)

const canContinue = computed(
  () =>
    hasPrescription.value === false ||
    (hasPrescription.value === true && hasCode.value !== null),
)

const selectPrescription = (value) => {
  hasPrescription.value = value
  if (!value) {
    hasCode.value = null
  }
  euStore.prescriptionQuestionAnswers = {
    hasPrescription: hasPrescription.value,
    hasCode: hasCode.value,
  }
}

const selectCode = (value) => {
  hasCode.value = value
  euStore.prescriptionQuestionAnswers = {
    hasPrescription: hasPrescription.value,
    hasCode: hasCode.value,
  }
}

const handleContinue = () => {
  if (hasPrescription.value === false) {
    Tracker.trackButtonSelect('no_prescription', 'prescription_question')
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_no_prescription',
    })
    navigateNext('noPrescription')
  } else if (hasCode.value === true) {
    Tracker.trackButtonSelect('has_prescription', 'prescription_question')
    Tracker.trackButtonSelect('has_code', 'prescription_question')
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_prescription',
    })
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_code',
    })
    navigateNext('hasCode')
  } else {
    Tracker.trackButtonSelect('has_prescription', 'prescription_question')
    Tracker.trackButtonSelect('no_code', 'prescription_question')
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_prescription',
    })
    apiRequest.sendSubscriptionActivationEntry({
      type: 'user_has_no_code',
    })
    navigateNext('uploadPrescription')
  }
}
</script>

<style lang="scss" scoped>
fieldset {
  display: flex;
  flex-direction: column;
  gap: $kds-space-1x;

  .quiz-radio {
    margin: 0;
  }
}
</style>
